/**
 * THE DOCUMENTS TAB — `tasks/practice-documents.md` §1.4, §3.1, §7 phase 1, Phase 8.
 *
 * A practice uploads the forms it already has, and configures what should happen to a patient who
 * receives one. Everything on this screen is CONFIGURATION: §7 puts manual send in Phase 2, the
 * invite in Phase 3 and appointment types in Phase 4, so nothing here hands a document to anybody.
 *
 * Phase 4 (§24.5) adds the fourth delivery route — APPOINTMENT TRIGGERS — and it is still
 * configuration: this component writes the rule, `runSendDueDocumentsJob` (§24.4, another wave)
 * is what eventually acts on it. Every string in that block is therefore future tense.
 *
 * ── WHERE THE "NOT YET DELIVERED" NOTICE WENT ────────────────────────────────────────────────
 *
 * It used to be a standing banner above the accordion. It is now only in the EDIT DIALOG, which is
 * where the delivery flags it is about actually live — a banner over a LIST is answering a question
 * nobody asked while they were reading it, and the sentence lands where the toggles are. The copy
 * itself is unchanged and still lives in `practice-document-copy.ts`.
 *
 * ── THE HOUSE PATTERN, AND THE SHAPE THE FIRST REBUILD GOT WRONG ─────────────────────────────
 *
 * This tab used to be its own thing — a bespoke list with an inline editor that replaced the whole
 * screen. It was then rebuilt as ONE PANEL PER DOCUMENT, which is still not the house pattern:
 * `consent-form-settings.component.html:16-40` is the real analogue for a settings tab holding a
 * list of N items, and it does the opposite — **fixed, named sections that contain lists**, an add
 * icon in each section header, and per-item icon buttons in an `action-heading`.
 *
 * The difference is not cosmetic. With ZERO documents, one-panel-per-document renders nothing at
 * all: the accordion is invisible and the tab reads as a bare title, a notice and a stray button.
 * A fixed section still draws its header and an empty body, so the page has structure before it
 * has data — and the add action has somewhere to live.
 *
 * ── WHY THE TWO SECTIONS ARE `requiresResponse` ──────────────────────────────────────────────
 *
 * §14.2. A document the practice is WAITING ON and a sheet they hand out are different things to
 * everybody who uses this screen — different delivery, different record, different dedupe — and the
 * split is the same one consent makes between onboarding and procedure forms. The add icon in each
 * header opens the upload dialog PRE-SET to that section, so the flag is chosen by where the user
 * clicked rather than by a control they have to go looking for.
 *
 * Editing moved into two `MatDialog`s for the same reason those tabs use dialogs: a settings tab is
 * a list of what is configured, and configuring is a decision you take and come back from. The
 * inline editor made "which documents do we have" and "what does this one do" the same screen, so
 * neither could be read without leaving the other.
 *
 * ── THE ONE BUG THIS FILE EXISTED TO CAUSE, AND NO LONGER DOES ───────────────────────────────
 *
 * The empty state used to be `*ngIf="!isLoading && rows.length === 0"`, which says nothing about
 * `listError`. A failed list request sets `rows` to `[]`, so a practice whose request had just
 * failed was shown the error banner AND "No documents yet. Upload a PDF and it will appear here."
 * at the same time. **A failed load is not an empty list.** Telling a practice they have no
 * documents when the request failed is worse than saying nothing at all — they will upload a second
 * copy of something they already have. `sectionEmptyState` is a single private method that BOTH
 * sections' getters go through, rather than a longer `*ngIf` in each of them, precisely so the
 * exclusivity is one assertable thing rather than two conditions someone extends one of later.
 *
 * ── WHAT IS TRANSLATED AND WHAT IS NEVER PRINTED ─────────────────────────────────────────────
 *
 * `fillMode` is translated, never printed, and `hasAcroForm` / `hasTextLayer` are not user-facing —
 * they only explain WHY a document is download-only. Both live in `practice-document-copy.ts` now
 * that the panel and the settings dialog both say them; the long-form reasoning is in that file's
 * header.
 */

import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GroupService } from '../../../core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import {
  PracticeDocumentDto, PracticeDocumentLinkDto, PracticeDocumentService,
} from '../../../core/services/practice-document.service';
import {
  NO_LINKS_SENTENCE, PAUSE_LINK_TOOLTIP, REMOVE_LINK_TOOLTIP, RESUME_LINK_TOOLTIP,
  deliveryReachSentence, documentCategories, expiryLabel, patientOutcomeSentence, patientOutcomeIcon,
  downloadOnlyReason, linkChipLabel, linkOutcomeMessage, linkRefusalCode, linkRefusalMessage,
  removeLinkConfirmation,
} from './practice-document-copy';
import { PracticeDocumentPdfChoice } from './practice-document-pdf-choice';
import {
  PracticeDocumentEditDialogComponent, PracticeDocumentEditDialogResult,
} from './practice-document-edit-dialog/practice-document-edit-dialog.component';
import {
  PracticeDocumentTriggerDialogComponent, PracticeDocumentTriggerDialogResult,
} from './practice-document-trigger-dialog/practice-document-trigger-dialog.component';
import {
  PracticeDocumentUploadDialogComponent, PracticeDocumentUploadDialogResult,
} from './practice-document-upload-dialog/practice-document-upload-dialog.component';

/**
 * One metadata chip on a document row: a self-contained fact, an icon that hints at what kind of
 * fact it is, and a tooltip for the caption a chip has no room to print.
 */
export interface PracticeDocumentFact {
  icon: string;
  label: string;
  tooltip: string;
}

@Component({
  selector: 'app-practice-document-settings',
  templateUrl: './practice-document-settings.component.html',
  styleUrls: ['./practice-document-settings.component.scss'],
})
export class PracticeDocumentSettingsComponent extends BaseComponent implements OnInit {

  selectedGroup: any = null;
  rows: PracticeDocumentDto[] = [];
  isLoading = false;
  listError: string | null = null;

  /* ── §24.2 appointment triggers ─────────────────────────────────────────────────────────── */

  readonly noLinksSentence = NO_LINKS_SENTENCE;
  readonly removeLinkTooltip = REMOVE_LINK_TOOLTIP;

  /**
   * A refusal from a link WRITE, held against the document it was written for.
   *
   * Not a toast, for the reason Phase 1c settled about file refusals: the sentence has to sit
   * beside the control that caused it, or a user reads "that appointment type no longer exists"
   * three seconds after their eyes have moved on. Keyed by document so a refusal cannot be shown
   * under a row it has nothing to do with.
   */
  linkActionError: { documentId: string; message: string } | null = null;

  /**
   * Per document: what its links are, whether they are still coming, and whether they failed.
   *
   * All three, not just the array — for exactly the reason `sectionEmptyState` exists. "No
   * appointment type sends this document" is a claim about the practice's data and it cannot be
   * made from an empty array alone, because a failed request and a document with no triggers
   * produce the same empty array. See `linkEmptyState`.
   */
  private linkStates: {
    [documentId: string]: { loading: boolean; error: string | null;
      links: PracticeDocumentLinkDto[]; };
  } = {};

  /**
   * The documents whose panels have been opened at least once, which is not the same set as the
   * keys of `linkStates` — that one is cleared every time the list is re-read.
   *
   * Never pruned. An id that is no longer in `rows` costs a string and is filtered out at the one
   * place this is read; pruning it would mean reacting to a deletion in a second place.
   */
  private openedDocuments = new Set<string>();

  /** A state unit (tasks/state-mode.md): documents are never sent to patients, so there are no appointment triggers. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private practiceDocumentService: PracticeDocumentService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (group: any) => {
          this.selectedGroup = group;
          if (!group) return;
          this.loadDocuments();
        },
        error: () => {
          this.isLoading = false;
          this.listError = 'Your practice could not be loaded, so documents cannot be shown.';
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The list
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  loadDocuments(): void {
    this.isLoading = true;
    this.listError = null;

    this.practiceDocumentService.listDocuments()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.rows = response.data || [];
          this.isLoading = false;
          this.resetLinkStates();
        },
        error: (error) => {
          this.rows = [];
          this.isLoading = false;
          // No row survives, so no link state may either: a chip left behind from the previous
          // load would describe a document that is no longer on the screen.
          this.linkStates = {};
          this.listError = PracticeDocumentPdfChoice.messageFor(error,
            'The practice\'s documents could not be loaded.');
        },
      });
  }

  /* ── The two sections (§14.2) ───────────────────────────────────────────────────────────── */

  /**
   * `!== false`, not `=== true`.
   *
   * `requiresResponse` is NOT NULL with a default of 1 (§16), so the only way it arrives absent is
   * from a serializer that has not caught up. Read this way round, a document the practice expects
   * back lands in the section that says so; read the other way round it would appear as an
   * information sheet, which is §14.2's silent failure exactly.
   */
  get respondingRows(): PracticeDocumentDto[] {
    return this.rows.filter((row) => row.requiresResponse !== false);
  }

  get informationRows(): PracticeDocumentDto[] {
    return this.rows.filter((row) => row.requiresResponse === false);
  }

  /**
   * "No documents yet" is a CLAIM ABOUT THE PRACTICE'S DATA, and it can only be made when the
   * request that would have contradicted it actually succeeded.
   *
   * `rows` is emptied on a failure so no row describes a stale document, which is right — and it
   * is exactly why this cannot be `rows.length === 0` alone. The error banner already says what
   * happened; a second sentence underneath it saying the practice has nothing is not an extra
   * detail, it is a contradiction of the banner above it.
   *
   * ONE method, two callers. Two sections means two empty states, and an exclusivity that held in
   * one of them and not the other would be the same bug with half the symptoms.
   */
  private sectionEmptyState(rows: PracticeDocumentDto[]): boolean {
    return !this.isLoading && !this.listError && rows.length === 0;
  }

  get showRespondingEmptyState(): boolean {
    return this.sectionEmptyState(this.respondingRows);
  }

  get showInformationEmptyState(): boolean {
    return this.sectionEmptyState(this.informationRows);
  }

  /**
   * THE CATEGORIES THIS PRACTICE HAS ALREADY USED, for the two dialogs to suggest.
   *
   * Derived from `rows` rather than fetched: `listDocuments()` is called with no filters, so the
   * list behind this screen is every document the practice has, active and inactive alike — which
   * is exactly the set a category should be suggested from. A second endpoint would be a second
   * answer to a question this component already holds the answer to.
   *
   * Read at the moment a dialog opens, not held: a category invented in one dialog is on the next
   * list read, and this getter is what the following dialog will see.
   */
  get documentCategories(): string[] {
    return documentCategories(this.rows);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * What will happen to a patient who receives this — the part that matters
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  patientOutcomeSentence(document: PracticeDocumentDto): string {
    return patientOutcomeSentence(document);
  }

  downloadOnlyReason(document: PracticeDocumentDto): string | null {
    return downloadOnlyReason(document);
  }

  patientOutcomeIcon(document: PracticeDocumentDto): string {
    return patientOutcomeIcon(document);
  }

  /**
   * The delivery routes that are actually configured, as chips.
   *
   * An empty list is a real answer and says so in its own words rather than rendering an empty
   * row: a document nobody is asked for is a document that does nothing, and that is worth being
   * able to see at a glance from the collapsed list.
   */
  deliveryRoutes(document: PracticeDocumentDto): string[] {
    const routes: string[] = [];
    if (document.sendWithInvite) routes.push('Goes out with the patient invite');
    // The hard block is only meaningful while the document is on the invite at all — the toggle's
    // own hint says so, and a chip claiming it in isolation would overstate what is configured.
    if (document.sendWithInvite && document.requiredInInvite) {
      routes.push('Blocks the invite until it comes back');
    }
    if (document.allowManualSend) routes.push('Staff may send it by hand');
    return routes;
  }

  /**
   * WILL ANYTHING EVER ASK A PATIENT FOR THIS DOCUMENT — as a sentence, or `null`.
   *
   * This used to read the three delivery FLAGS and nothing else, which stopped being true the day
   * §24.5 made appointment triggers the fourth delivery route: a document with every flag off and
   * a live trigger goes out on every booking, while the row claimed directly above those chips
   * that nobody would ever be asked for it.
   *
   * ── A CLAIM THAT NEEDS THE TRIGGERS NEEDS THE TRIGGERS TO HAVE ARRIVED ──────────────────────
   *
   * `null` while the read is outstanding or failed, and this is the same rule as `showNoLinks` and
   * `sectionEmptyState` — the third time this component has had to make it. All three of these
   * sentences are claims about the practice's configuration, and an empty `links` array is what a
   * failed request and a document with no triggers both look like. Saying nothing for the moment
   * it takes to find out is the only answer that cannot be wrong.
   */
  deliveryReachSentence(document: PracticeDocumentDto): string | null {
    if (!document) return null;

    const state = this.linkState(document.id);
    if (state.loading || state.error) return null;

    return deliveryReachSentence(this.deliveryRoutes(document).length, state.links);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * APPOINTMENT TRIGGERS — §3.2, §24.2, §24.5
   *
   * The fourth delivery route, and the first one with a shape of its own: the other three are
   * booleans on the document, this is a LIST of rules, each of which is an appointment type, a
   * moment, and an offset. It is rendered where the other three are rendered — in the row, as
   * chips — because a practice asking "what happens to this document" is asking one question, and
   * an answer split across a panel and a dialog is two answers.
   *
   * ── WHY THE WRITES ARE IMMEDIATE AND THE DELIVERY FLAGS ARE NOT ─────────────────────────────
   *
   * §17 put the ten delivery flags in a dialog because they are ONE form with one Save: half of
   * them applied is a state the practice never asked for. A trigger is not like that. Each one is
   * a complete decision on its own — add this rule, pause that rule, delete the other — and there
   * is no Save that could make two of them atomic. `custom-form-settings.component.html:236-289`
   * is the precedent in this very folder and it links inline for the same reason.
   *
   * ── PAUSE IS A POST AND REMOVE IS A DELETE, AND THEY MUST NOT LOOK THE SAME ─────────────────
   *
   * §3.2 warns about it in as many words. Both make a trigger stop firing; only one is
   * recoverable. The POST is an UPSERT on the unique quadruple that finds INACTIVE rows too, so
   * pausing and resuming is the same call with `active` flipped, and a resumed trigger is the same
   * row rather than a second one. The copy at every one of those controls says which is which —
   * see `practice-document-copy.ts`.
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * ONE READ PER DOCUMENT THE PRACTICE ACTUALLY OPENS — not one per document they have.
   *
   * §24.2's list endpoint is per document, so the tab used to fire a GET for every row the moment
   * the list came back. A practice with fifty documents paid fifty requests to draw a screen on
   * which every one of those answers was collapsed out of sight. Now the list read is followed by
   * nothing, and `onDocumentOpened` asks for a document's triggers the first time its panel opens.
   *
   * ── WHY AN OPEN PANEL IS RE-READ AND A CLOSED ONE IS NOT ────────────────────────────────────
   *
   * `linkStates` is rebuilt rather than merged for the reason it always was: a document that has
   * just been deleted must not keep its chips, and a re-read of the list is exactly when a row can
   * vanish. But a PANEL does not close because the list behind it was re-read — `trackBy` keeps
   * the same panel instance, so `(opened)` does not fire a second time. Clearing the state and
   * stopping there would leave a row the practice is looking at showing its loading bar forever.
   *
   * So the rows that are open are re-read here, and only those: laziness is about the documents
   * nobody has looked at, not about refusing to refresh the one they have.
   */
  private resetLinkStates(): void {
    this.linkStates = {};
    this.rows
      .filter((row) => this.openedDocuments.has(row.id))
      .forEach((row) => this.loadLinks(row.id));
  }

  /**
   * A panel opening is the request for its triggers, and only the FIRST opening is.
   *
   * `linkStates[id]` is set synchronously by `loadLinks`, so the guard covers the in-flight case
   * as well as the answered one — opening, closing and reopening a panel while the GET is still
   * outstanding cannot start a second.
   */
  onDocumentOpened(document: PracticeDocumentDto): void {
    if (!document) return;
    // Remembered even when the guard below returns: this is what `resetLinkStates` reads to decide
    // which rows are worth re-reading after the list changes underneath them.
    this.openedDocuments.add(document.id);
    if (this.linkStates[document.id]) return;
    this.loadLinks(document.id);
  }

  loadLinks(documentId: string): void {
    this.linkStates[documentId] = { loading: true, error: null, links: [] };

    this.practiceDocumentService.listAppointmentTypeLinks(documentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.linkStates[documentId] = {
            loading: false, error: null, links: response.data || [],
          };
        },
        error: (error) => {
          this.linkStates[documentId] = {
            loading: false,
            // NOT an empty list. See `linkEmptyState`.
            error: linkRefusalMessage(error,
              'This document\'s appointment triggers could not be loaded.'),
            links: [],
          };
        },
      });
  }

  /**
   * The default is LOADING, not "none".
   *
   * A document whose links have not been asked for yet is indistinguishable, from an array alone,
   * from one that has none — and the sentence this feeds says a booking will never ask a patient
   * for the document. Defaulting to `loading: true` makes the unknown case silent rather than
   * wrong, which is the same direction §17's empty-state fix chose.
   */
  private linkState(documentId: string)
    : { loading: boolean; error: string | null; links: PracticeDocumentLinkDto[] } {
    return this.linkStates[documentId] || { loading: true, error: null, links: [] };
  }

  linksFor(document: PracticeDocumentDto): PracticeDocumentLinkDto[] {
    return this.linkState(document.id).links;
  }

  isLoadingLinks(document: PracticeDocumentDto): boolean {
    return this.linkState(document.id).loading;
  }

  linkLoadError(document: PracticeDocumentDto): string | null {
    return this.linkState(document.id).error;
  }

  /**
   * A FAILED LOAD IS NOT AN EMPTY LIST — §17's defect, one level down.
   *
   * The tab already learned this about its document list: `rows` is emptied on failure, so a row
   * count alone said "no documents yet" underneath the error banner that contradicted it. Links
   * are the same shape and the claim is worse, because it is specific: "no appointment type sends
   * this document" tells a practice their configuration is missing and invites them to add a
   * second copy of a trigger that already exists.
   */
  showNoLinks(document: PracticeDocumentDto): boolean {
    const state = this.linkState(document.id);
    return !state.loading && !state.error && state.links.length === 0;
  }

  linkChipLabel(link: PracticeDocumentLinkDto): string {
    return linkChipLabel(link);
  }

  pauseTooltip(link: PracticeDocumentLinkDto): string {
    return link.active === false ? RESUME_LINK_TOOLTIP : PAUSE_LINK_TOOLTIP;
  }

  pauseIcon(link: PracticeDocumentLinkDto): string {
    return link.active === false ? 'play_circle_outline' : 'pause_circle_outline';
  }

  /** The refusal held against THIS document, and no other. */
  linkErrorFor(document: PracticeDocumentDto): string | null {
    return this.linkActionError && this.linkActionError.documentId === document.id
      ? this.linkActionError.message
      : null;
  }

  /* ── Adding one ─────────────────────────────────────────────────────────────────────────── */

  /**
   * THE ADD FORM IS A DIALOG NOW — see `practice-document-trigger-dialog.component.ts`.
   *
   * §24.5 kept the trigger controls in the row so that "what happens to this document" was one
   * answer in one place. The ANSWER still is: the chips, their pause and their remove never left.
   * What left is the four-control form for composing a rule that does not exist yet, which was
   * drawn under every document in both sections to serve an action taken once per document.
   *
   * The dialog owns the POST, exactly as the upload and edit dialogs own theirs, so §24.5's other
   * requirement survives the move: a refusal leaves the controls as they were typed instead of
   * closing over them. What comes back is the upsert's response, because only this tab can act on
   * it — the toast and the re-read are both its own.
   */
  startAddingLink(document: PracticeDocumentDto): void {
    this.linkActionError = null;

    const ref = this.dialog.open(PracticeDocumentTriggerDialogComponent, {
      width: '620px',
      maxWidth: '96vw',
      autoFocus: false,
      data: { document },
    });

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PracticeDocumentTriggerDialogResult | undefined) => {
        if (!result) return;

        // WHICH of the three things the upsert did. The status is 200 either way, so the booleans
        // are the only evidence — and "resumed" is a different fact from "added" to a practice who
        // is about to go looking for the duplicate they think they made.
        this.showToastMessage(linkOutcomeMessage(result.outcome));

        // Re-read rather than push the response onto the array: the POST is an UPSERT, so what
        // came back may be a row that was already there (paused, and now live again) rather than
        // a new one. Appending it would draw the same trigger twice.
        this.loadLinks(document.id);
      });
  }

  /* ── Pausing, and removing ──────────────────────────────────────────────────────────────── */

  /**
   * PAUSE / RESUME — the POST, with `active` flipped.
   *
   * The same quadruple is re-sent deliberately: §24.2's upsert finds the existing row, including
   * an inactive one, so this updates a trigger rather than creating a second. Sending the offset
   * back exactly as it was stored is what keys it — recomputing it from a unit here would be the
   * off-by-seven §3.2 warns about, arriving at the one call that cannot afford to miss the row.
   */
  toggleLinkPaused(document: PracticeDocumentDto, link: PracticeDocumentLinkDto): void {
    this.linkActionError = null;

    this.practiceDocumentService.linkAppointmentType(document.id, {
      appointmentTypeId: link.appointmentTypeId,
      sendOn: link.sendOn,
      offsetDays: link.offsetDays,
      active: link.active === false,
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadLinks(document.id),
        error: (error) => {
          this.linkActionError = {
            documentId: document.id,
            message: linkRefusalMessage(error, 'That appointment trigger could not be changed.'),
          };
        },
      });
  }

  /**
   * REMOVE — the hard DELETE (§24.2), confirmed first, and the confirmation names the pause it is
   * not. Nothing else on this screen destroys configuration outright.
   */
  async removeLink(document: PracticeDocumentDto, link: PracticeDocumentLinkDto): Promise<void> {
    const confirmed = await this.showConfirmationDialog(
      'Remove appointment trigger',
      removeLinkConfirmation(link, document.name),
      undefined, 'Cancel', 'Remove', true);
    if (!confirmed) return;

    this.linkActionError = null;

    this.practiceDocumentService.unlinkAppointmentType(document.id, link.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadLinks(document.id),
        error: (error) => {
          this.linkActionError = {
            documentId: document.id,
            message: linkRefusalMessage(error, 'That appointment trigger could not be removed.'),
          };
          // THE ONE REFUSAL WHOSE ANSWER IS TO GO AND LOOK AGAIN. Two people on the same settings
          // page: the other one removed this trigger first. Leaving the chip drawn would show a
          // rule that no longer exists, and the practice would keep pressing a button that keeps
          // failing. The list is re-read so the chip goes, and the sentence says so.
          if (linkRefusalCode(error) === 'APPOINTMENT_TYPE_LINK_NOT_FOUND') {
            this.loadLinks(document.id);
          }
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The two dialogs
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Upload, then configure. The upload dialog can only send the three fields the create endpoint
   * accepts, so the settings dialog opens on the row that comes back — a document is never left
   * sitting in the list with delivery flags nobody has looked at.
   */
  openUploadDialog(event: Event | null, requiresResponse: boolean): void {
    // The add icon lives in a `mat-expansion-panel-header`, which toggles on any click inside it.
    // Without this, adding a document would also collapse the section it was being added to — the
    // same reason the per-row edit button is not in a header at all any more.
    if (event) event.stopPropagation();

    const ref = this.dialog.open(PracticeDocumentUploadDialogComponent, {
      width: '620px',
      maxWidth: '96vw',
      autoFocus: false,
      // §14.2, decided by WHERE THE USER CLICKED rather than by a toggle buried in the dialog.
      // `categories` is a SUGGESTION LIST and nothing else — see `documentCategories`.
      data: { requiresResponse, categories: this.documentCategories },
    });

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PracticeDocumentUploadDialogResult | undefined) => {
        if (!result) return;
        this.loadDocuments();
        this.openEditDialog(null, result.document);
      });
  }

  /**
   * The edit button on a row.
   *
   * The `event` parameter survives the restructure and is still passed by anything that opens this
   * from inside a `mat-expansion-panel-header`: that header toggles on any click inside it, so an
   * icon that did not stop propagation would open the dialog AND collapse the section underneath
   * it. The row's own buttons sit in the panel BODY and pass `null`.
   */
  openEditDialog(event: Event | null, document: PracticeDocumentDto): void {
    if (event) event.stopPropagation();

    const ref = this.dialog.open(PracticeDocumentEditDialogComponent, {
      width: '680px',
      maxWidth: '96vw',
      autoFocus: false,
      data: { document, categories: this.documentCategories },
    });

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PracticeDocumentEditDialogResult | undefined) => {
        // Only a dialog that actually wrote something makes the list stale. A replacement is
        // written the moment it is uploaded, so `changed` covers Close as well as Save.
        if (result && result.changed) this.loadDocuments();
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Open the blank, and delete
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * The signed URL is fetched at the moment it is opened — one held all afternoon has expired.
   *
   * `event` for the reason `openEditDialog` takes one: this button lives in a
   * `mat-expansion-panel-header` now, and a header toggles on any click inside it. Without the
   * stop, opening the blank would also collapse the document it belongs to.
   */
  openBlank(event: Event | null, document: PracticeDocumentDto): void {
    if (event) event.stopPropagation();
    this.practiceDocumentService.getFileUrl(document.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const url = response && response.data ? response.data.url : null;
          if (!url) {
            this.showToastMessage('That document could not be opened.');
            return;
          }
          window.open(url, '_blank');
        },
        error: (error) => {
          this.showToastMessage(
            PracticeDocumentPdfChoice.messageFor(error, 'That document could not be opened.'));
        },
      });
  }

  /**
   * DELETE — soft (§3.1 is paranoid), and confirmed first.
   *
   * The confirmation says what survives, because "delete" over a document patients have already
   * returned reads as though their completed copies go with it. They do not: those live in the
   * patient record (§0 decision 5) and this removes the blank from the practice's list.
   */
  async deleteDocument(event: Event | null, document: PracticeDocumentDto): Promise<void> {
    if (event) event.stopPropagation();
    const confirmed = await this.showConfirmationDialog(
      'Delete document',
      `Delete "${document.name}"? It stops being offered to patients. Copies patients have `
      + 'already completed stay in their records.',
      undefined, 'Cancel', 'Delete', false);
    if (!confirmed) return;

    this.practiceDocumentService.deleteDocument(document.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage(`"${document.name}" deleted.`);
          this.loadDocuments();
        },
        error: (error) => {
          this.showToastMessage(
            PracticeDocumentPdfChoice.messageFor(error, 'The document could not be deleted.'));
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Labels
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * `typeof … !== 'number'` rather than a null check, in both of these.
   *
   * §9 decision 4 makes `fileSizeBytes` and `pageCount` nullable *derived* metadata: "could not
   * tell" must not render as zero, and it must not render as an empty "page(s)" either. A missing
   * key from an endpoint that simply did not send one is the same fact as a stored NULL, and one
   * test covers both without a comparison the compiler would call unintentional.
   */
  fileSizeLabel(document: PracticeDocumentDto): string | null {
    if (!document || typeof document.fileSizeBytes !== 'number') return null;
    return `${PracticeDocumentPdfChoice.megabytes(document.fileSizeBytes)} MB`;
  }

  pageCountLabel(document: PracticeDocumentDto): string | null {
    if (!document || typeof document.pageCount !== 'number') return null;
    return `${document.pageCount} page(s)`;
  }

  /**
   * THE ROW'S METADATA, AS ONE CHIP STRIP.
   *
   * This used to be seven `<strong>Label:</strong> value` paragraphs — Category, File, Pages, Size,
   * Link expires after, Ask again after — and nothing else in `group/components` prints metadata
   * that way. `note-type-settings` and `consent-form-settings` both give an item a heading, its
   * icon buttons, and then ONE body: a chip list or a preview. Seven labelled lines per document
   * meant a practice with five documents had to scroll past 35 lines of file sizes to reach the
   * delivery routes, which are the only part of the row they can act on.
   *
   * ── WHY A LABEL PER FACT WOULD BE WRONG ─────────────────────────────────────────────────────
   *
   * A chip has no room for a caption beside its value, so each label here is a COMPLETE fact:
   * `expiryLabel` returns a sentence rather than a number for exactly that reason,
   * and the two facts that are self-evident from their value — the file name and the category —
   * carry an icon and a tooltip instead. Nothing in this list is a schema value: `fillMode`,
   * `hasAcroForm` and `hasTextLayer` stay where they were, in the sentence above the chips.
   *
   * ── WHAT IS OMITTED, AND WHAT NEVER IS ──────────────────────────────────────────────────────
   *
   * Category, page count and file size are dropped when absent: a chip reading "Pages: —" claims
   * the inspection ran and found nothing, and §9 decision 4 is explicit that "could not tell" must
   * not render as a value. Expiry and the repeat rule are ALWAYS drawn, because both are decisions
   * with a default rather than facts that can be missing, and a practice wondering why a returning
   * patient was never asked again needs to see "Asked once, ever" without opening the dialog.
   */
  documentFacts(document: PracticeDocumentDto): PracticeDocumentFact[] {
    const facts: PracticeDocumentFact[] = [];
    if (!document) return facts;

    if (document.category) {
      facts.push({ icon: 'folder_open', label: document.category, tooltip: 'Category' });
    }

    facts.push({
      icon: 'picture_as_pdf',
      label: document.mediaFileName,
      tooltip: 'The blank that was uploaded',
    });

    const pages = this.pageCountLabel(document);
    if (pages) facts.push({ icon: 'auto_stories', label: pages, tooltip: 'Length of the blank' });

    const size = this.fileSizeLabel(document);
    if (size) facts.push({ icon: 'sd_storage', label: size, tooltip: 'Size of the blank' });

    facts.push({
      icon: 'schedule',
      label: expiryLabel(document.expiryDays),
      tooltip: 'How long a patient’s link stays usable once it has been sent',
    });

    return facts;
  }

  trackById(index: number, item: { id: string }): string {
    return item.id;
  }
}
